import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

export const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleFaq = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqs = [
    {
      q: 'O que é o InfoForge?',
      a: 'O InfoForge é uma plataforma de engenharia de infoprodutos assistida por inteligência artificial. Diferente de chats generalistas, o InfoForge orquestra pesquisa estratégica, arquitetura pedagógica, escrita com checagem de fatos, diagramação de ebooks (PDF e EPUB), workbooks práticos e copywriting comercial de forma totalmente integrada.'
    },
    {
      q: 'Para quem a plataforma é indicada?',
      a: 'Especialistas técnicos, consultores, criadores de conteúdo e agências de coprodução que desejam transformar seu conhecimento em produtos digitais de alta densidade técnica e acabamento visual profissional, sem passar semanas em tarefas manuais dispersas.'
    },
    {
      q: 'Preciso saber programar ou dominar engenharia de prompts?',
      a: 'Não. O InfoForge abstrai toda a complexidade técnica de prompts e arquitetura em uma esteira guiada. Você só precisa definir sua área de especialidade, o público pretendido e validar os direcionamentos estratégicos sugeridos pelo sistema.'
    },
    {
      q: 'Quem é dono dos direitos autorais do conteúdo gerado?',
      a: 'Você. Todos os infoprodutos, textos, estruturas, materiais complementares e arquivos compilados gerados através da plataforma pertencem 100% ao criador da conta, com liberdade total para comercialização, distribuição ou registro.'
    },
    {
      q: 'Quais formatos físicos são entregues atualmente?',
      a: 'Ebooks diagramados em formato PDF de alta resolução, arquivos EPUB para leitores digitais (Kindle, Apple Books), Workbooks em PDF com exercícios práticos, roteiros de VSL em texto estruturado, sequências de e-mails, copies para páginas de vendas e pacotes ZIP consolidados com checagem de integridade SHA-256.'
    },
    {
      q: 'Como funciona o sistema de créditos?',
      a: 'Os créditos são a moeda de processamento da plataforma. Cada etapa do pipeline (pesquisa, blueprint, capítulos do ebook, copy, etc.) consome créditos com dedução atômica e transparente apenas após a validação e entrega do estágio.'
    },
    {
      q: 'Posso editar e revisar o conteúdo após a compilação?',
      a: 'Sim. Você pode intervir em cada etapa do pipeline antes da compilação física, além de exportar os textos e arquivos finais para realizar quaisquer ajustes manuais que desejar.'
    },
    {
      q: 'O recurso de vídeo generativo e clonagem de voz já está disponível ao vivo?',
      a: 'Não no momento. Os roteiros de vídeo (VSL em 3 atos) já são gerados com estrutura de cena e gancho persuasivo, mas a renderização de vídeo generativo e voz sintética neural encontra-se atualmente em fase de certificação laboratorial e será liberada em marcos futuros do nosso roadmap.'
    },
    {
      q: 'Como funciona o processo de seleção do Private Beta?',
      a: 'Liberamos acessos em lotes controlados com base nas informações submetidas no formulário de solicitação. Analisamos o nicho, o tipo de infoproduto pretendido e a prontidão para compartilhar feedback sobre a experiência de uso.'
    },
    {
      q: 'Quando haverá lançamento com cadastro público irrestrito?',
      a: 'O cadastro público só será aberto após a conclusão de todos os ciclos de certificação do Private Beta e validação da infraestrutura de alta concorrência. Até lá, a entrada permanece estritamente restrita a usuários com convite.'
    }
  ];

  return (
    <section id="faq" className="py-20 md:py-28 bg-brand-surface/30 border-t border-brand-border relative">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-xs font-semibold uppercase tracking-wider text-brand-cyan px-3 py-1 rounded-full bg-brand-cyan/10 border border-brand-cyan/20">
            Dúvidas Frequentes
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mt-4 mb-5 tracking-tight">
            Perguntas & Respostas Factualmente Transparentes
          </h2>
          <p className="text-base sm:text-lg text-brand-textMuted leading-relaxed">
            Esclarecimentos detalhados sobre as capacidades da plataforma, propriedade intelectual e modelo de acesso.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, idx) => (
            <div
              key={idx}
              className="rounded-2xl bg-brand-surface border border-brand-border overflow-hidden transition-all duration-200"
            >
              <button
                onClick={() => toggleFaq(idx)}
                className="w-full px-6 py-5 text-left flex items-center justify-between gap-4 hover:bg-brand-surfaceLight/30 transition-colors focus:outline-none focus:ring-1 focus:ring-brand-primary"
                aria-expanded={openIndex === idx}
              >
                <span className="text-base font-semibold text-white">
                  {faq.q}
                </span>
                <ChevronDown
                  className={`w-5 h-5 text-brand-textSubtle shrink-0 transition-transform duration-300 ${
                    openIndex === idx ? 'rotate-180 text-brand-cyan' : ''
                  }`}
                />
              </button>

              {openIndex === idx && (
                <div className="px-6 pb-6 pt-2 text-sm text-brand-textMuted leading-relaxed border-t border-brand-border/40 bg-brand-bg/40">
                  {faq.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
